
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Save, X } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

const PositTaskFlag = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [response, setResponse] = useState("");

  const { data: taskFlag, isLoading } = useQuery({
    queryKey: ["posit-task-flag", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posit_task_flags")
        .select(`
          id,
          posit_id,
          user_id,
          question,
          context,
          status,
          response,
          processed,
          created_at
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) {
        throw error;
      }
      return data;
    },
  });

  const updateTaskFlagMutation = useMutation({
    mutationFn: async ({ response, status }: { response?: string; status: string }) => {
      const updateData = {
        status,
        processed: true,
        ...(response && { response })
      };

      const { error } = await supabase
        .from("posit_task_flags")
        .update(updateData)
        .eq("id", id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posit-task-flags"] });
      toast({
        title: "Success",
        description: "Task flag updated successfully",
      });
      navigate(`/posits/${taskFlag?.posit_id}`);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update task flag",
      });
    },
  });

  const handleSubmit = () => {
    if (!response.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a response before submitting",
      });
      return;
    }
    updateTaskFlagMutation.mutate({ response, status: "closed" });
  };

  const handleClose = () => {
    updateTaskFlagMutation.mutate({ status: "cancelled" });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!taskFlag) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold">Task not found</h1>
      </div>
    );
  }

  const isProcessing = updateTaskFlagMutation.isPending;
  const isClosed = taskFlag.status === "closed" || taskFlag.status === "cancelled";

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-3xl">
      <Button 
        variant="ghost" 
        onClick={() => navigate(`/posits/${taskFlag.posit_id}`)} 
        className="mb-4 sm:mb-6 -ml-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Posit
      </Button>

      <div className="space-y-4 sm:space-y-6">
        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h1 className="text-xl sm:text-2xl font-bold">Task Details</h1>
            <Badge variant={isClosed ? "secondary" : "default"}>
              {taskFlag.status || (taskFlag.processed ? "closed" : "open")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Created on {format(new Date(taskFlag.created_at), "MMM d, yyyy")}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <h2 className="font-medium">Posit Response</h2>
            <p className="text-muted-foreground text-sm sm:text-base">{taskFlag.question}</p>
          </div>

          <div className="space-y-2">
            <h2 className="font-medium">Question</h2>
            <p className="text-muted-foreground text-sm sm:text-base">{taskFlag.context}</p>
          </div>

          {!isClosed && (
            <div className="space-y-2">
              <h2 className="font-medium">Your Response</h2>
              <Textarea 
                value={response} 
                onChange={(e) => setResponse(e.target.value)} 
                placeholder="Type your response here..." 
                className="min-h-[100px]" 
                disabled={isProcessing}
              />
            </div>
          )}

          {taskFlag.response && (
            <div className="space-y-2">
              <h2 className="font-medium">Submitted Response</h2>
              <p className="text-muted-foreground text-sm sm:text-base">{taskFlag.response}</p>
            </div>
          )}
        </div>

        {!isClosed && (
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 sm:pt-6">
            <Button 
              variant="outline" 
              onClick={handleClose} 
              disabled={isProcessing}
              className="order-2 sm:order-1"
            >
              <X className="h-4 w-4 mr-2" />
              Close Without Response
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isProcessing}
              className="order-1 sm:order-2"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Submit Response
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PositTaskFlag;
